import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { CellContext } from "@tanstack/react-table";

import { Button, Col, Row } from "reactstrap";
import ReactBSAlert from "react-bootstrap-sweetalert";

import {
  PublishPage,
  DeletePendingPage,
  clearMessage,
  getNonPublishedPages,
  getPublishedPages,
} from "@/services/pendingPagesServices/pendingPagesActions";
import NotificationAlertOptions from "@/layouts/Alerts";
import { configCurrentSite } from "@/utils/config";
import DisplayPendingPagesView from "./DisplayPendingPagesView";

/**
 * A pending-page queue entry as the two GET /api/pendingpage lists return it,
 * with `page` (and its template) and `user` populated. `page` is null when the
 * populate's soft-delete filter drops a deleted page (SCRUM-822). `published`
 * and `updatedAt` are always set (schema default / timestamps).
 */
export type PendingPageRow = {
  _id: string;
  published: boolean;
  updatedAt: string;
  page?: {
    _id?: string;
    global_title?: string;
    page_template?: { name?: string } | null;
    page_configuration?: { path?: string };
  } | null;
  user?: { name?: string } | null;
};

type PendingPageCell = CellContext<PendingPageRow, unknown>;
type PendingPageTextCell = CellContext<PendingPageRow, string>;

const DisplayPendingPagesContainer = () => {
  const dispatch = useAppDispatch();

  // Store slices
  const pendingState = useAppSelector((s) => s.pendingPages);
  const site = useAppSelector((s) => s.sites?.site);
  const permission = useCurrentPermission();

  // UI state
  const [alertNode, setAlertNode] = useState<React.ReactNode>(null);
  const [collapseOpen, setCollapseOpen] = useState(true);

  // Initial load
  useEffect(() => {
    dispatch(getNonPublishedPages());
    dispatch(getPublishedPages());
  }, [dispatch]);

  // Success messages -> toast + clear
  useEffect(() => {
    if (pendingState?.message) {
      NotificationAlertOptions("success", pendingState.message);
      dispatch(clearMessage());
    }
  }, [pendingState?.message, dispatch]);

  // SCRUM-1151: both lists are handed to TableWrapper whole. The updatedAt sort
  // stays here and feeds `data`, so most-recent-first remains the default order
  // while the headers additionally become sortable. The page indices, the page
  // size, the debounced search box and the two `.slice()`s that used to live
  // here are gone — react-table owns them, and it clamps the page index against
  // its row model, which is what stops a shrinking list from going blank.
  const byUpdatedAtDesc = (list: PendingPageRow[]) =>
    [...list].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

  const nonPublishedPages = useMemo(
    () => byUpdatedAtDesc(pendingState?.nonPublishedPages || []),
    [pendingState?.nonPublishedPages]
  );

  const publishedPages = useMemo(
    () => byUpdatedAtDesc(pendingState?.publishedPages || []),
    [pendingState?.publishedPages]
  );

  // Actions
  const onClickPublishToggle = useCallback(
    async (row: PendingPageRow, isPublishedHistory: boolean) => {
      // SCRUM-822: `page` is a populated ref to a soft-deletable Page, so the
      // plugin's `match:{isDeleted:{$ne:true}}` can resolve it to null. Reading
      // `_id` off null would throw INSIDE the try below, where the bare catch
      // swallowed it: the click would do nothing at all and say nothing either.
      // The row itself already renders (the title cell is optional-chained), so
      // the toggle is reachable. Today the controller filters these rows out
      // before they reach the client; this stops the view depending on that.
      //
      // SCRUM-1261: the message is per table, because this handler is shared by
      // both and only the pending queue renders "Remove from queue" — telling a
      // published-history user to press a button that table does not have is a
      // worse dead end than the silent no-op SCRUM-822 replaced. Neither wording
      // claims the page is gone: soft delete sets `isDeleted: true`
      // (models/plugins/softDelete.ts), which is a restorable state, and there is
      // no restore control in the admin to point at either.
      if (!row?.page?._id) {
        NotificationAlertOptions(
          "error",
          isPublishedHistory
            ? "This entry's page was deleted, so it cannot be published."
            : "This entry's page was deleted. Remove this entry from the queue."
        );
        return;
      }
      try {
        // SCRUM-840: object arg + .unwrap() so a failed publish surfaces via the
        // central hub instead of an unhandled rejection.
        await dispatch(
          PublishPage({ pageId: row.page._id, ppageId: row._id })
        ).unwrap();
      } catch {
        /* central hub surfaces the error */
      }
    },
    [dispatch]
  );

  const hideAlert = useCallback(() => setAlertNode(null), []);

  const confirmDelete = useCallback(
    (element: PendingPageRow) => {
      setAlertNode(
        // @ts-expect-error react-bootstrap-sweetalert prop-types
        <ReactBSAlert
          custom
          style={{ display: "block", marginTop: "-100px" }}
          title="Would you like to remove this pending page?"
          customIcon={
            <div className="swal2-icon swal2-question swal2-animate-question-icon" style={{ display: "flex" }}>
              <span className="swal2-icon-text">?</span>
            </div>
          }
          onConfirm={async () => {
            hideAlert();
            try {
              // SCRUM-840: only claim success once the delete resolves; the
              // central hub surfaces the error on failure.
              await dispatch(DeletePendingPage(element._id)).unwrap();
              NotificationAlertOptions("success", "Entry removed");
            } catch {
              /* central hub surfaces the error */
            }
          }}
          onCancel={hideAlert}
          confirmBtnBsStyle="default"
          confirmBtnText="Ok"
          btnSize=""
        >
          This only removes the entry; the page itself is not deleted or unpublished.
        </ReactBSAlert>
      );
    },
    [dispatch, hideAlert]
  );

  const toggleCollapse = useCallback(() => setCollapseOpen((v) => !v), []);

  const previewHref = useCallback((element: PendingPageRow) => {
    const domain = configCurrentSite.CURRENT_DOMAIN_SITE || "";
    const origin = /^https?:\/\//i.test(domain)
      ? domain.replace(/\/+$/, "")
      : "https://" + domain.replace(/\/+$/, "");
    return `${origin}/${(element.page?.page_configuration?.path || "").replace(/^\/+/, "")}`;
  }, []);

  /**
   * SCRUM-1151: the container describes the columns for both tables now.
   *
   * The title column carries an `accessorFn`, not only a `cell`. TableWrapper's
   * global filter searches the columns that expose a value, and the search box
   * this replaces matched `global_title` OR — when that was empty — the template
   * name. Without the fallback in the accessor, a queue entry that has no global
   * title would silently stop being findable while the list looked unchanged.
   */
  const columnsFor = useCallback(
    ({ variant }: { variant: "pending" | "published" }) => {
      const isPublishedHistory = variant === "published";
      const canEdit = !!(permission?.publish || permission?.edit);

      const PublishedCell = ({ row }: PendingPageCell) =>
        row.original?.published ? (
          <i className="fas fa-check text-success fa-1x" />
        ) : (
          <i className="ni ni-fat-remove text-warning fa-1x" />
        );
      PublishedCell.displayName = "PendingPagePublishedCell";

      /*
        md="auto" (col-md-auto), NOT a fixed md="3" — SCRUM-1079. col-md-3 is 25%
        of this narrow Actions cell — 67px measured — while "Remove from queue"
        is ~183px, so the button overflowed its own column and the NEXT column
        was painted over the overflowing part. document.elementFromPoint at the
        button's centre then returned the publish toggle, and clicking the middle
        of "Remove from queue" PUBLISHED the page instead of removing it from the
        queue. col-md-auto sizes each column to its content, so the controls no
        longer overlap (they wrap if the cell is too narrow, which stays
        clickable). Moving the cluster into a table cell does not change that
        geometry, so the markup is carried over unchanged.
      */
      const ActionsCell = ({ row }: PendingPageCell) => {
        const element = row.original;
        return (
          <Row>
            {isPublishedHistory ? (
              <Col md="auto">
                <a
                  className="btn-round btn-icon btn btn-primary btn-sm"
                  id="btn_view"
                  style={{ textDecoration: "none" }}
                  href={previewHref(element)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="btn-inner--icon mr-1">
                    <i className="fas fa-magnifying-glass" />
                  </span>
                  <span className="btn-inner--text">Preview</span>
                </a>
              </Col>
            ) : null}

            {canEdit ? (
              <>
                {isPublishedHistory ? null : (
                  <Col md="auto">
                    <Button
                      className="btn-round btn-icon"
                      color="danger"
                      // SCRUM-1160: this cell renders once per queued page, so a
                      // constant id repeats across the document. Keyed on the row
                      // like the tooltip targets elsewhere, an unscoped
                      // `#btn_delete_...` lookup can no longer silently act on
                      // whichever row happens to be first.
                      id={`btn_delete_${element._id}`}
                      onClick={() => confirmDelete(element)}
                      size="sm"
                    >
                      <span className="btn-inner--icon mr-1">
                        <i className="fas fa-trash" />
                      </span>
                      <span className="btn-inner--text">Remove from queue</span>
                    </Button>
                  </Col>
                )}

                <Col md="auto">
                  <label className="custom-toggle custom-toggle-primary mr-1">
                    <input
                      checked={element.published}
                      type="checkbox"
                      onChange={() => onClickPublishToggle(element, isPublishedHistory)}
                    />
                    <span
                      className="custom-toggle-slider rounded-circle"
                      data-label-off="publish"
                      data-label-on="publish"
                    />
                  </label>
                </Col>
              </>
            ) : null}
          </Row>
        );
      };
      ActionsCell.displayName = isPublishedHistory
        ? "PublishedPageActionsCell"
        : "PendingPageActionsCell";

      return [
        {
          id: "title",
          header: "Page title",
          accessorFn: (row: PendingPageRow) =>
            row?.page?.global_title || row?.page?.page_template?.name || "",
          cell: ({ getValue }: PendingPageTextCell) => <span className="text-muted">{getValue()}</span>,
        },
        {
          id: "updatedBy",
          header: "Updated By",
          accessorFn: (row: PendingPageRow) => row?.user?.name || "",
          cell: ({ getValue }: PendingPageTextCell) => <span className="text-muted">{getValue()}</span>,
        },
        { id: "published", header: "Published", cell: PublishedCell },
        { id: "actions", header: "Actions", cell: ActionsCell },
      ];
    },
    [permission, onClickPublishToggle, confirmDelete, previewHref]
  );

  const pendingColumns = useMemo(
    () => columnsFor({ variant: "pending" }),
    [columnsFor]
  );
  const publishedColumns = useMemo(
    () => columnsFor({ variant: "published" }),
    [columnsFor]
  );

  const ViewModel = useMemo(
    () => ({
      // alert
      alertNode,

      // permissions/site
      permission,
      site,

      // pending table
      nonPublishedPages,
      pendingColumns,

      // published table
      publishedPages,
      publishedColumns,

      // accordion
      collapseOpen,
      toggleCollapse,
    }),
    [
      alertNode,
      permission,
      site,
      nonPublishedPages,
      pendingColumns,
      publishedPages,
      publishedColumns,
      collapseOpen,
      toggleCollapse,
    ]
  );

  return <DisplayPendingPagesView {...ViewModel} />;
};

export default DisplayPendingPagesContainer;
